import { useMemo, type ReactNode } from "react";
import {
  getObservabilityRuntime,
  type MetricWindow,
  type RuntimeMetricsResp,
  type RuntimePodMetricsResp,
} from "@/api";
import { ChartCard } from "@/components/metric";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { MetricLineChart } from "./charts";
import { ObservabilityStateBlock } from "./ObservabilityStateBlock";
import {
  describeMetricUnit,
  describeSeriesCoverage,
  formatBytesValue,
  formatCount,
  formatMillicores,
  formatMillis,
  toRuntimeSeries,
  type MetricFormatter,
  type MetricUnitView,
} from "./observabilityModel";
import { useObservabilityMetric } from "./useObservabilityMetric";
import styles from "./ObservabilityPage.module.css";

/**
 * API pod 런타임이다. pod 별 창 요약 카드와 pod 별 라인을 표시한다.
 *
 * pod 을 합계로 합치지 않는다. replica 하나에만 메모리 누수가 있으면 합계에서는 평균에 가려진다.
 * 그러면 그 pod 을 재시작해야 한다는 결론을 얻을 수 없다.
 */
export function ObservabilityRuntimePanel({ window }: { window: MetricWindow }): ReactNode {
  const { state, reload } = useObservabilityMetric(getObservabilityRuntime, window);
  return (
    <ObservabilityStateBlock
      state={state}
      loadingText="런타임 지표를 불러오고 있습니다."
      onRetry={reload}
    >
      {(data) => <RuntimeBody data={data} window={window} />}
    </ObservabilityStateBlock>
  );
}

function RuntimeBody({
  data,
  window,
}: {
  data: RuntimeMetricsResp;
  window: MetricWindow;
}): ReactNode {
  const coverage = useMemo(
    () => describeSeriesCoverage(data.points, window),
    [data.points, window],
  );

  const memory = useMemo(
    () => toRuntimeSeries(data.points, (point) => point.memory_working_set_bytes),
    [data.points],
  );
  const lag = useMemo(
    () => toRuntimeSeries(data.points, (point) => point.event_loop_lag_ms),
    [data.points],
  );

  // pod 들이 500점 상한을 나눠 쓴다. 캡션이 pod 당 해상도를 표시하지 않으면 "이 pod 만 데이터가
  // 드물다" 는 틀린 결론이 나온다.
  const caption =
    memory.perPodNote === "" ? coverage.caption : `${coverage.caption} · ${memory.perPodNote}`;

  if (data.pods.length === 0) {
    return <EmptyState size="lg">이 구간에 표본이 없습니다.</EmptyState>;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.podGrid}>
        {data.pods.map((pod) => (
          <PodCard key={pod.pod_name} pod={pod} />
        ))}
      </div>

      <ChartCard
        title="메모리 working set"
        subtitle={caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={memory.series}
          formatTick={formatDateTime}
          formatValue={formatBytesValue}
          ariaLabel="pod 별 메모리 추이"
        />
      </ChartCard>

      <ChartCard
        title="event loop lag"
        subtitle={caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={lag.series}
          formatTick={formatDateTime}
          formatValue={formatMillis}
          ariaLabel="pod 별 event loop lag 추이"
        />
      </ChartCard>
    </div>
  );
}

/**
 * pod 카드 한 장이다. 끝난 pod 은 흐리게 표시하고, 언제까지 있었는지 적는다.
 *
 * 카드 수는 replica 수가 아니다. 창 안에서 한 번이라도 관측된 pod 은 모두 카드 한 장씩 가진다.
 * 구분하지 않으면 24시간 창의 카드 8장이 "지금 replica 가 8개" 로 읽힌다. 실제로는 살아 있는
 * 2개와 롤아웃 3회에서 없어진 6개였다. 값은 지우지 않는다. 이 화면은 재시작 직전의 메모리
 * 상태를 알려주어야 한다.
 *
 * `!== false` 로 판정하는 이유: 배포 창의 구 BE 는 이 키를 보내지 않는다.
 * `undefined` 를 끝난 것으로 읽으면 정상 pod 이 모두 흐리게 표시된다.
 */
function PodCard({ pod }: { pod: RuntimePodMetricsResp }): ReactNode {
  // @compat ZERG-559 until=be:96221b28
  const live = pod.active !== false;
  return (
    <ChartCard
      title={pod.pod_name}
      inactive={!live}
      subtitle={live ? undefined : lastSeenText(pod.last_seen_at)}
      action={live ? undefined : <span className={styles.podStateBadge}>종료됨</span>}
    >
      <PodMetric label="메모리" unit={pod.memory_working_set_bytes} format={formatBytesValue} />
      <PodMetric label="CPU" unit={pod.cpu_millicores} format={formatMillicores} />
      <PodMetric label="event loop lag" unit={pod.event_loop_lag_ms} format={formatMillis} />
      <PodMetric label="DB pool 점유" unit={pod.db_pool_checked_out} format={formatCount} />
      <PodMetric
        label={upstreamPoolLabel(pod.upstream_pool_max)}
        unit={pod.upstream_pool_active}
        format={formatCount}
      />
    </ChartCard>
  );
}

/**
 * 끝난 pod 의 부제다. 이 pod 이 언제까지 있었는지 표시한다.
 *
 * 문구가 `마지막 수집` 인 이유: 이 화면의 다른 문구와 같은 시각을 가리키기 때문이다.
 * 시각을 보내지 않는 구 BE 응답에서는 없는 시각을 만들지 않는다.
 *
 * @compat ZERG-559 until=be:96221b28
 */
function lastSeenText(lastSeenAt: string | null | undefined): string | undefined {
  const atMs = parseUtcMs(lastSeenAt ?? null);
  return atMs === null ? undefined : `마지막 수집 ${formatDateTime(atMs)}`;
}

/**
 * 상류 pool 라벨이다. 라벨이 한도를 표시한다.
 *
 * 점유만 보이면 "2 가 많은가" 를 판단할 수 없다. 한도는 설정값이라 창 안에서 상수다. 그래서
 * 값·최대·산포에 매번 붙이지 않고 라벨에 한 번만 적는다. 한도를 읽지 못한 런타임에서는
 * 없는 수를 만들지 않고 라벨만 표시한다.
 *
 * `== null` 로 비교하는 이유: 배포 창의 구 BE 는 이 키를 보내지 않는다. `===` 로 비교해
 * `undefined` 를 놓치면 라벨에 "한도 -" 가 표시된다.
 */
function upstreamPoolLabel(maximum: number | null): string {
  // @compat ZERG-546 until=be:0c26057c
  return maximum == null ? "상류 pool 점유" : `상류 pool 점유 (한도 ${formatCount(maximum)})`;
}

function PodMetric({
  label,
  unit,
  format,
}: {
  label: string;
  unit: Parameters<typeof describeMetricUnit>[0];
  format: MetricFormatter;
}): ReactNode {
  const view: MetricUnitView = describeMetricUnit(unit, format);
  return (
    <>
      <div className={styles.podMetric}>
        <span className={styles.podMetricLabel}>{label}</span>
        <span className={styles.podMetricValue}>{view.value}</span>
      </div>
      {view.hint === "" ? null : <div className={styles.podMetricHint}>{view.hint}</div>}
    </>
  );
}
